import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getBrowseContext, isFordBrand, pushEvent } from '../utils/dataLayer';

function VehicleSelectorModal({ accessoryId, onClose }) {
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [availableModels, setAvailableModels] = useState([]);
    const [availableVariants, setAvailableVariants] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [loading3dConfig, setLoading3dConfig] = useState(false);
    const confirmedRef = useRef(false);

    const applyDefaultModelAndVariant = (data, models) => {
        const variantsByModel = data?.variants_by_model || {};
        const defaultModelFromApi = data?.default_model || null;
        const defaultVariantFromApi = data?.default_variant || null;

        // Fallback preference when API does not send defaults:
        // 1) Bronco model (if present), else 2) first model
        const broncoModel = (models || []).find((m) => /bronco/i.test((m?.name || '').trim()));
        const fallbackModel = broncoModel || (models && models.length > 0 ? models[0] : null);
        const modelToSelect = defaultModelFromApi
            ? (models.find((m) => String(m.id) === String(defaultModelFromApi.id)) || defaultModelFromApi)
            : fallbackModel;

        if (!modelToSelect) {
            setSelectedModel(null);
            setSelectedVariant(null);
            setAvailableVariants([]);
            return;
        }

        setSelectedModel(modelToSelect);
        const language = localStorage.getItem('language') || 'en';
        const variants = [...(variantsByModel[modelToSelect.id] || [])].sort((a, b) =>
            String(a?.name || '').localeCompare(String(b?.name || ''), language, { sensitivity: 'base' })
        );
        setAvailableVariants(variants);

        // Fallback preference when API does not send default variant:
        // 1) Big Bend variant (if present), else 2) first variant
        const bigBendVariant = variants.find((v) => /big\s*bend/i.test((v?.name || '').trim()));
        const variantToSelect = defaultVariantFromApi
            ? (variants.find((v) => String(v.id) === String(defaultVariantFromApi.id)) || null)
            : (bigBendVariant || variants[0] || null);
        setSelectedVariant(variantToSelect);
    };

    useEffect(() => {
        if (!accessoryId) return;
        let cancelled = false;
        const load = async () => {
            setLoading3dConfig(true);
            try {
                const country_id = localStorage.getItem('country_id');
                const dealer_id = localStorage.getItem('dealer_id');
                const brand_id = localStorage.getItem('brand_id') || process.env.REACT_APP_BRAND_ID;
                const market_id = localStorage.getItem('market_id') || '1';
                const token = 'Bearer ' + process.env.REACT_APP_FORD_API_TOKEN;
                const response = await axios.get(
                    `${process.env.REACT_APP_FORD_API_URL}/api/v1/accessories/${accessoryId}/3d-config?dealer_id=${dealer_id}&brand_id=${brand_id}&country_id=${country_id}&market_id=${market_id}`,
                    { headers: { 'Authorization': token, 'X-Locale': localStorage.getItem('language') || 'en' } }
                );
                if (cancelled) return;
                if (response.data.available) {
                    const data = response.data;
                    setConfig(data);
                    const language = localStorage.getItem('language') || 'en';
                    const models = [...(data.models || [])].sort((a, b) =>
                        String(a?.name || '').localeCompare(String(b?.name || ''), language, { sensitivity: 'base' })
                    );
                    setAvailableModels(models);
                    applyDefaultModelAndVariant(data, models);
                }
            } catch (err) {
                console.error('Error loading 3D config:', err);
            } finally {
                if (!cancelled) setLoading3dConfig(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [accessoryId]);

    const load3dConfig = async () => {
        if (!accessoryId) return;

        setLoading3dConfig(true);
        try {
            const country_id = localStorage.getItem('country_id');
            const dealer_id = localStorage.getItem('dealer_id');
            const brand_id = localStorage.getItem('brand_id') || process.env.REACT_APP_BRAND_ID;
            const market_id = localStorage.getItem('market_id') || '1';

            const token = 'Bearer ' + process.env.REACT_APP_FORD_API_TOKEN;
            const response = await axios.get(
                `${process.env.REACT_APP_FORD_API_URL}/api/v1/accessories/${accessoryId}/3d-config?dealer_id=${dealer_id}&brand_id=${brand_id}&country_id=${country_id}&market_id=${market_id}`,
                {
                    headers: {
                        'Authorization': token,
                        'X-Locale': localStorage.getItem('language') || 'en'
                    }
                }
            );

            if (response.data.available) {
                const data = response.data;
                setConfig(data);
                const language = localStorage.getItem('language') || 'en';
                const models = [...(data.models || [])].sort((a, b) =>
                    String(a?.name || '').localeCompare(String(b?.name || ''), language, { sensitivity: 'base' })
                );
                setAvailableModels(models);
                applyDefaultModelAndVariant(data, models);
            }
        } catch (error) {
            console.error('Error loading 3D config:', error);
        } finally {
            setLoading3dConfig(false);
        }
    };

    const handleModelChange = (modelId) => {
        const model = availableModels.find(m => m.id === parseInt(modelId, 10));
        setSelectedModel(model);
        setSelectedVariant(null);
        setAvailableVariants([]);

        if (model && config) {
            const variantsByModel = config.variants_by_model || {};
            const language = localStorage.getItem('language') || 'en';
            const variants = [...(variantsByModel[model.id] || [])].sort((a, b) =>
                String(a?.name || '').localeCompare(String(b?.name || ''), language, { sensitivity: 'base' })
            );
            setAvailableVariants(variants);
        }
    };

    const handleDismiss = () => {
        if (!confirmedRef.current && isFordBrand()) {
            pushEvent('site_3d_vehicle_selector_cancelled', {
                accessory_id: accessoryId,
                ...getBrowseContext(),
            });
        }
        onClose();
    };

    const handleOpen3dViewer = () => {
        if (!selectedModel || !selectedVariant) {
            alert('Please select both model and variant');
            return;
        }

        confirmedRef.current = true;
        if (isFordBrand()) {
            const browse = getBrowseContext();
            pushEvent('site_3d_vehicle_selector_confirmed', {
                accessory_id: accessoryId,
                model_id: selectedModel.id,
                model_name: selectedModel.name,
                variant_id: selectedVariant.id,
                variant_name: selectedVariant.name,
                year_id: browse.year_id,
                year_name: browse.year_name,
                category_id: browse.category_id,
                category_name: browse.category_name,
            });
        }

        // Full page load to 3D viewer - releases memory when returning (no SPA state accumulation)
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        const url = `/3d-viewer/${accessoryId}?model_id=${selectedModel.id}&variant_id=${selectedVariant.id}&return_url=${returnUrl}&_3d=1`;
        window.location.href = url;
        onClose();
    };

    return (
        <div
            className="modal-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
            }}
            onClick={handleDismiss}
        >
            <div
                className="modal-content vehicle-selector-modal-content"
                style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '30px',
                    maxWidth: '720px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxSizing: 'border-box'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0 }}>
                        <span language="ar">اختر سيارتك</span>
                        <span language="en">Select your Vehicle</span>
                    </h2>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="modal-3d-form-close"
                        aria-label="Close"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {loading3dConfig ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p>
                            <span language="ar">جاري التحميل...</span>
                            <span language="en">Loading...</span>
                        </p>
                    </div>
                ) : (
                    <div className="modal-3d-form">
                        <div className="modal-3d-form-field modal-3d-form-field--model">
                            <select
                                className="modal-3d-form-select"
                                value={selectedModel?.id || ''}
                                onChange={(e) => handleModelChange(e.target.value)}
                                style={{
                                    width: '100%',
                                    fontSize: '14px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            >
                                <option value="">
                                    {(document.documentElement?.lang || localStorage.getItem('language') || 'en') === 'ar' ? 'اختر الطراز' : 'Select Model'}
                                </option>
                                {availableModels.map(model => (
                                    <option key={model.id} value={model.id}>
                                        {model.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-3d-form-field modal-3d-form-field--variant">
                            <select
                                className="modal-3d-form-select"
                                value={selectedVariant?.id || ''}
                                onChange={(e) => {
                                    const variant = availableVariants.find(v => v.id === parseInt(e.target.value, 10));
                                    setSelectedVariant(variant);
                                }}
                                disabled={!selectedModel}
                                style={{
                                    fontSize: '14px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    opacity: !selectedModel ? 0.6 : 1
                                }}
                            >
                                <option value="">
                                    {(document.documentElement?.lang || localStorage.getItem('language') || 'en') === 'ar' ? 'اختر النوع' : 'Select Variant'}
                                </option>
                                {availableVariants.map(variant => (
                                    <option key={variant.id} value={variant.id}>
                                        {variant.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-3d-form-field modal-3d-form-actions">
                            <button
                                onClick={handleOpen3dViewer}
                                disabled={!selectedModel || !selectedVariant}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    background: selectedModel && selectedVariant ? 'var(--ford-skyview, #0066cc)' : '#ccc',
                                    color: 'white',
                                    cursor: selectedModel && selectedVariant ? 'pointer' : 'not-allowed',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    borderRadius: '20px'
                                }}
                            >
                                <span language="ar">منظر 3D ثلاثي الأبعاد</span>
                                <span language="en">View 3D</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default VehicleSelectorModal;
